import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  MapPin,
  Navigation,
  Home,
  Loader2,
  ExternalLink,
  ChevronDown,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { catColor, CategoryIcon } from "@/lib/categories";
import MiniMap from "@/components/MiniMap";

function DataRow({ icon: Icon, label, children, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
        <Icon size={12} /> {label}
      </p>
      <div className="font-medium text-slate-800">{children}</div>
    </div>
  );
}

export default function DetailsView() {
  const { permid } = useParams();
  const navigate = useNavigate();
  const { authed } = useAuth();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    let ok = true;
    api
      .getObject(permid)
      .then(({ data }) => ok && setItem(data))
      .catch(() => ok && setItem(null))
      .finally(() => ok && setLoading(false));    return () => {
      ok = false;
    };
  }, [permid]);

  if (loading) {
    return (
      <div className="grid h-[100dvh] w-full place-items-center bg-slate-50">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="grid h-[100dvh] w-full place-items-center bg-slate-50 p-6 text-center">
        <div>
          <p className="font-display text-xl font-bold text-slate-800">Nicht gefunden</p>
          <button
            onClick={() => navigate("/admin")}
            className="mt-4 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
          >
            Zurück zur Karte
          </button>
        </div>
      </div>
    );
  }

  const color = catColor(item.category);
  const showImage = item.hasImage && !imgError;

  return (
    <div className="fixed inset-0 z-50 flex h-[100dvh] w-full flex-col bg-slate-50">
      {/* Header */}
      <header className="z-10 flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white/90 px-3 py-3 backdrop-blur">
        <button
          data-testid="back-button"
          onClick={() => navigate("/admin")}
          className="grid h-10 w-10 place-items-center rounded-xl text-slate-600 transition-colors hover:bg-slate-100"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="flex-1 truncate text-left font-display text-lg font-bold text-slate-900">
          {item.title || "Ohne Titel"}
        </h1>
        {authed && (
          <button
            data-testid="edit-button"
            onClick={() => navigate(`/edit/${permid}`)}
            className="flex items-center gap-2 rounded-xl bg-primary px-3.5 py-2 text-sm font-semibold text-white transition-transform hover:scale-[1.03] active:scale-[0.97]"
          >
            <Pencil size={15} /> Bearbeiten
          </button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl">
        {/* Hero */}
        {showImage ? (
          <a
            href={api.imageUrl(permid)}
            target="_blank"
            rel="noreferrer"
            title="Bild in voller Größe öffnen"
            data-testid="detail-image-link"
            className="block cursor-zoom-in"
          >
            <img
              src={api.imageUrl(permid)}
              onError={() => setImgError(true)}
              alt={item.title}
              className="h-[30vh] max-h-72 w-full rounded-b-3xl object-cover shadow-sm"
              data-testid="detail-image"
            />
          </a>
        ) : (
          <div
            className="grid h-[26vh] max-h-64 w-full place-items-center rounded-b-3xl"
            style={{
              background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
            }}
          >
            <CategoryIcon category={item.category} color="#ffffff" size={64} />
          </div>
        )}

        <div className="space-y-4 p-5">
          {/* Category badge */}
          {item.category && (
            <span
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold text-white"
              style={{ background: color }}
              data-testid="detail-category"
            >
              <CategoryIcon category={item.category} color="#fff" size={15} />
              {item.category}
            </span>
          )}

          {item.address && (
            <DataRow icon={Home} label="Adresse" testid="detail-address">
              {item.address}
            </DataRow>
          )}

          {item.threeWords && (
            <DataRow icon={MapPin} label="what3words" testid="detail-w3w">
              <a
                href={`https://what3words.com/${item.threeWords}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-primary hover:underline"
              >
                <span className="font-semibold text-red-500">///</span>
                {item.threeWords}
                <ExternalLink size={13} />
              </a>
            </DataRow>
          )}

          <button
            type="button"
            onClick={() => setShowMap((s) => !s)}
            data-testid="toggle-map"
            className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left transition-colors hover:bg-slate-50"
          >
            <p className="mb-1.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">
              <span className="flex items-center gap-1.5">
                <Navigation size={12} /> Koordinaten
              </span>
              <span className="flex items-center gap-1 normal-case tracking-normal text-primary">
                {showMap ? "Karte ausblenden" : "Karte anzeigen"}
                <ChevronDown
                  size={13}
                  className={`transition-transform ${showMap ? "rotate-180" : ""}`}
                />
              </span>
            </p>
            <span className="coord-box text-sm font-medium text-slate-800">
              {item.latitude != null ? Number(item.latitude).toFixed(6) : "–"},{" "}
              {item.longitude != null ? Number(item.longitude).toFixed(6) : "–"}
            </span>
          </button>

          {showMap && item.latitude != null && (
            <div className="overflow-hidden rounded-2xl border border-slate-200" data-testid="detail-map">
              <MiniMap
                lat={item.latitude}
                lng={item.longitude}
                category={item.category}
                className="gomap aspect-square w-full"
              />
            </div>
          )}

          <p
            className="pt-1 text-center text-[10px] tracking-wide text-slate-300"
            data-testid="detail-permid"
          >
            ID: {permid}
          </p>
        </div>
        </div>
      </div>
    </div>
  );
}
